import React from "react";
import { useT } from "../../../../core/i18n";
import {
  formatSapDate,
  formatSapDateTime,
  formatSwfFieldDisplay,
  getStatusBadgeClass,
  getStatusLabel,
  getSwfDocuments,
  getSwfRemarks,
  isSwfFieldVisible,
  openSwfDocument,
} from "./staffWelfare.service";
import { toast } from "react-toastify";
import Api from "../../../../core/data/redux/utils/apiCall";

type Props = {
  detail: any;
  formConfig: any;
};

const SwfDetailsBody: React.FC<Props> = ({ detail, formConfig }) => {
  const { t } = useT();
  const L = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  const purpose = detail?.purpose || detail?.purposeCode || "";
  const isNtab = purpose === "NTAB" || purpose === "Tablet PC to dependent daughter";
  const documents = getSwfDocuments(detail);
  const remarks = getSwfRemarks(detail);
  const employeeName =
    detail?.employeeName ||
    detail?.name ||
    [detail?.firstname, detail?.lsastname || detail?.lastname].filter(Boolean).join(" ") ||
    "-";

  const handleOpenDoc = async (doc: any) => {
    try {
      await openSwfDocument(doc);
    } catch (err: any) {
      toast.error(err?.message || Api.getErrorMessage(err) || "Failed to open document");
    }
  };

  const attachmentItems = isNtab
    ? [
        L("graduation_marksheet_document", "Final Marksheet of Graduation Course of Daughter Showing Marks Obtained / Final Percentage"),
        L("pg_admission_document", "PG Admission document"),
        L("tablet_bill_document", "Tablet bill / invoice"),
        L(
          "swf_ntab_three_docs_required",
          "NOTE- All the three documents are to be compulsorily uploaded. In the absence of the same the request would be rejected."
        ),
      ]
    : [
        L(
          "attachment_instruction_marriage",
          "In case of Marriage gift, marriage card/marriage certificate must be attached- in case of Child Birth gift, Birth certificate/ Discharge summary must be attached"
        ),
        L(
          "attachment_instruction_school_children",
          "In case of Grant of assistance for school children, Passing marksheet must be attached- In case of Funeral assistance, Death certificate must be attached"
        ),
        L(
          "attachment_instruction_nursery_admission",
          "In case of first time admission in Nursery/KG/1st, Admission receipt must be attached"
        ),
        L(
          "attachment_instruction_dependent_daughter",
          "In case of Grant for dependent daughter for degree course, Passing marksheet must be attached"
        ),
        L(
          "attachment_instruction_handicapped_child",
          "In case of Quarterly grant for handicapped child, Disability certificate must be attached"
        ),
        L(
          "attachment_instruction_cancer_diet",
          "In case of Diet allowance for employees suffering from cancer, certificate must be attached"
        ),
        L(
          "attachment_instruction_long_sickness",
          "In case of Monthly grant for long sickness, certificate must be attached"
        ),
      ];

  return (
    <>
      <div className={`alert ${getStatusBadgeClass(detail?.requestStatus || detail?.status).includes("success") ? "alert-success" : "alert-secondary"} d-flex justify-content-between align-items-center`}>
        <strong>{t("request_status")}</strong>
        <span className={getStatusBadgeClass(detail?.requestStatus || detail?.status)}>
          {getStatusLabel(detail?.requestStatus || detail?.status, t)}
        </span>
      </div>

      <Section title={L("application_info", "Application Info")}>
        <Field label={t("reference_no")} value={detail?.referenceNumber || "-"} />
        <Field
          label={t("request_creation_date")}
          value={formatSapDate(detail?.requestCreationDate || detail?.created_at) || "-"}
        />
        <Field label={L("Last_Latest_Approved", "Last/Latest Approved")} value={detail?.approverUserID || detail?.approverName || "-"} />
        <Field label={t("Remark")} value={detail?.remarks || detail?.hRemarks || "-"} />
      </Section>

      <Section title={t("employee_details")}>
        <Field label={L("name", "Name")} value={employeeName} />
        <Field label={L("designation", "Designation")} value={detail?.designation || "-"} />
        <Field label={L("department", "Department")} value={detail?.department || "-"} />
        <Field label={L("date_of_joining", "Date of joining")} value={formatSapDate(detail?.dateOfJoining) || "-"} />
        <Field label={L("place_of_posting", "Place of posting")} value={detail?.placeOfPosting || "-"} />
        <Field label={L("payscale", "Payscale")} value={detail?.payScale || detail?.payscale || detail?.postcode || "-"} />
      </Section>

      {(formConfig?.sections || []).map((section: any, index: number) => {
        const sectionHasPurposeField = section.fields?.some(
          (f: any) => String(f.key || "").toLowerCase() === "purpose"
        );
        return (
          <Section key={index} title={t(section.title) === section.title ? section.title : t(section.title)}>
            {(section.fields || []).map((field: any, fieldIndex: number) => {
              if (!isSwfFieldVisible(field, detail || {})) return null;
              const displayed = formatSwfFieldDisplay(field, detail || {}, purpose);
              const label = t(displayed.label) === displayed.label ? displayed.label : t(displayed.label);
              return (
                <React.Fragment key={`${index}-${fieldIndex}-${field.key}`}>
                  {displayed.type === "checkbox" ? (
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className="ti ti-checkbox text-danger"></i>
                      <span>{label}</span>
                    </div>
                  ) : (
                    <Field label={label} value={displayed.value || "N/A"} />
                  )}
                  {!sectionHasPurposeField &&
                    (field.key === "payScale" || field.key === "payscale") && (
                      <Field
                        label={t("purpose")}
                        value={detail?.display_meta?.label || detail?.purpose || "N/A"}
                      />
                    )}
                </React.Fragment>
              );
            })}
          </Section>
        );
      })}

      {documents.length > 0 && (
        <Section title={t("uploaded_documents")}>
          {documents.map((doc: any, idx: number) => (
            <div key={idx} className="d-flex justify-content-between align-items-center border-bottom py-2">
              <span>{doc.fileName || doc.name || doc.typeOfAttachedDoc || `Document ${idx + 1}`}</span>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleOpenDoc(doc)}>
                {t("view_details")}
              </button>
            </div>
          ))}
        </Section>
      )}

      <Section title={t("mandatory_attachments")}>
        <ul className="mb-0 ps-3">
          {attachmentItems.map((item, idx) => (
            <li key={idx} className="mb-1">
              {item}
            </li>
          ))}
        </ul>
      </Section>

      <Section title={t("instructions")}>
        <p className="mb-0">
          {L(
            "quarterly_claims_instruction",
            "For Quarterly claims, Quarter and Year are to be chosen according to Calendar year and not financial year"
          )}
        </p>
      </Section>

      {remarks.length > 0 && (
        <Section title={t("previous_remarks")}>
          {remarks.map((remark: any, idx: number) => (
            <div key={idx} className="border rounded p-3 mb-2">
              <div className="d-flex justify-content-between">
                <strong>{remark.approverName || remark.name || "-"}</strong>
                <small className="text-muted">
                  {remark.saptimestamp
                    ? formatSapDateTime(remark.saptimestamp)
                    : formatSapDateTime(remark.createdAt) || "-"}
                </small>
              </div>
              <div className="small text-muted">{remark.approverUserID || remark.empID}</div>
              <div>{remark.remarks || "-"}</div>
            </div>
          ))}
        </Section>
      )}
    </>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="card border-0 shadow-sm mb-3">
    <div className="card-header bg-white">
      <h5 className="mb-0 fw-bold">{title}</h5>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value ?? "-"}</span>
  </div>
);

export default SwfDetailsBody;
